import * as React from 'react';
import { FunctionComponent, SVGProps } from 'react';
import { AccordionSummary, Box, Typography } from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import SingleSimulation from './SingleSimulation';
import { ReactComponent as capacitor_esr } from '../../../static/simulation_image/capacitor_esr.svg';
import { ReactComponent as capacitor_dc_bias } from '../../../static/simulation_image/capacitor_dc_bias.svg';
import { ReactComponent as capacitor_temp_rise } from '../../../static/simulation_image/capacitor_temp_rise.svg';
import { ReactComponent as film_cap_life_calculator } from '../../../static/simulation_image/film_cap_life_calculator.svg';
import { ReactComponent as power_inductor_temp } from '../../../static/simulation_image/power_inductor_temp.svg';
import { ReactComponent as tempreture_bias_effect } from '../../../static/simulation_image/tempreture_bias_effect.svg';
import { ReactComponent as wide_bandwidth } from '../../../static/simulation_image/wide_bandwidth.svg';
import { ReactComponent as EthicalIcon } from '../../../static/sustainability_image/ethical_icon.svg';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SalesAndResourcesSimulationToolsOverviewPreBuiltProjectsContent } from '../../../graphql/pimcore/modules/siteContent/types/SalesAndResourcesSimulationToolsContent';

interface KsimOverviewProps {
    cmsContent: SalesAndResourcesSimulationToolsOverviewPreBuiltProjectsContent;
}

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            {...other}
            style={{ flexGrow: 1 }}
        >
            {value === index && (
                <Box
                    sx={{
                        marginTop: '0.42rem',
                        flexGrow: 1,
                        display: 'flex',
                        flexDirection: 'column',
                    }}
                >
                    <Typography component="div" sx={{ width: '100%' }}>
                        {children}
                    </Typography>
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `vertical-tab-${index}`,
        'aria-controls': `vertical-tabpanel-${index}`,
    };
}

export interface simulationData {
    title: string;
    img: string;
    redirectLink: string;
    icon: FunctionComponent<SVGProps<SVGSVGElement>>;
    buttonText: string;
    isNavigate?: boolean;
}

const KsimOverview: FunctionComponent<KsimOverviewProps> = ({ cmsContent }) => {
    const [value, setValue] = React.useState(0);
    const { isScreenXxl, isScreenXl, isScreenLg, isScreenMd, isScreenSm, isScreenXs } =
        useResponsive();
    const [expanded, setExpanded] = React.useState<number | false>(false);

    const handleAccordionChange =
        (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
            setExpanded(isExpanded ? panel : false);
        };

    const handleChange = (event: React.SyntheticEvent, newValue: number) => {
        setValue(newValue);
    };

    const simulations: simulationData[] = [
        {
            title: cmsContent.ESRComparison.projectTitle.content,
            img: cmsContent.ESRComparison.projectImage.imageUrl,
            redirectLink: cmsContent.ESRComparison.linkButton.linkUrl,
            icon: capacitor_esr,
            buttonText: cmsContent.ESRComparison.linkButton.linkText,
        },
        {
            title: cmsContent.TemperatureBiasEffect.projectTitle.content,
            img: cmsContent.TemperatureBiasEffect.projectImage.imageUrl,
            redirectLink: cmsContent.TemperatureBiasEffect.linkButton.linkUrl,
            icon: tempreture_bias_effect,
            buttonText: cmsContent.TemperatureBiasEffect.linkButton.linkText,
        },
        {
            title: cmsContent.CapacitorDCBiasComparison.projectTitle.content,
            img: cmsContent.CapacitorDCBiasComparison.projectImage.imageUrl,
            redirectLink: cmsContent.CapacitorDCBiasComparison.linkButton.linkUrl,
            icon: capacitor_dc_bias,
            buttonText: cmsContent.CapacitorDCBiasComparison.linkButton.linkText,
        },
        {
            title: cmsContent.WideBandwidth.projectTitle.content,
            img: cmsContent.WideBandwidth.projectImage.imageUrl,
            redirectLink: cmsContent.WideBandwidth.linkButton.linkUrl,
            icon: wide_bandwidth,
            buttonText: cmsContent.WideBandwidth.linkButton.linkText,
        },
        {
            title: cmsContent.CapacitorTemperatureRise.projectTitle.content,
            img: cmsContent.CapacitorTemperatureRise.projectImage.imageUrl,
            redirectLink: cmsContent.CapacitorTemperatureRise.linkButton.linkUrl,
            icon: capacitor_temp_rise,
            buttonText: cmsContent.CapacitorTemperatureRise.linkButton.linkText,
        },
        {
            title: cmsContent.PowerInductorTemperatureRise.projectTitle.content,
            img: cmsContent.PowerInductorTemperatureRise.projectImage.imageUrl,
            redirectLink: cmsContent.PowerInductorTemperatureRise.linkButton.linkUrl,
            icon: power_inductor_temp,
            buttonText: cmsContent.PowerInductorTemperatureRise.linkButton.linkText,
        },
        {
            title: cmsContent.CeramicAging.projectTitle.content,
            img: cmsContent.CeramicAging.projectImage.imageUrl,
            redirectLink: cmsContent.CeramicAging.linkButton.linkUrl,
            icon: EthicalIcon,
            buttonText: cmsContent.CeramicAging.linkButton.linkText,
        },
        {
            title: cmsContent.FilmCapacitorLifeCalculator.projectTitle.content,
            img: cmsContent.FilmCapacitorLifeCalculator.projectImage.imageUrl,
            redirectLink: cmsContent.FilmCapacitorLifeCalculator.linkButton.linkUrl,
            icon: film_cap_life_calculator,
            buttonText: cmsContent.FilmCapacitorLifeCalculator.linkButton.linkText,
        },
        {
            title: cmsContent.AluminumCapacitorLifeCalculator.projectTitle.content,
            img: cmsContent.AluminumCapacitorLifeCalculator.projectImage.imageUrl,
            redirectLink: cmsContent.AluminumCapacitorLifeCalculator.linkButton.linkUrl,
            icon: EthicalIcon,
            buttonText: cmsContent.AluminumCapacitorLifeCalculator.linkButton.linkText,
        },
    ];

    const generateSimulation = (simulation: simulationData) => {
        return (
            <Accordion
                expanded={true}
                square
                sx={{
                    backgroundColor: '#FAE1D8',
                    width: isScreenLg ? '565px' : isScreenXl ? '760px' : '833px',
                }}
            >
                <AccordionDetails>
                    <SingleSimulation
                        title={simulation.title}
                        img={simulation.img}
                        redirectLink={simulation.redirectLink}
                        buttonText={simulation.buttonText}
                        isNavigate={simulation.isNavigate}
                    />
                </AccordionDetails>
            </Accordion>
        );
    };

    return (
        <>
            {isScreenLg || isScreenXl || isScreenXxl ? (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        height: '100%',
                        width: '100%',
                        marginBottom: '2rem',
                    }}
                >
                    <Tabs
                        orientation="vertical"
                        value={value}
                        onChange={handleChange}
                        aria-label="Vertical tabs example"
                        TabIndicatorProps={{ style: { display: 'none' } }}
                        sx={{
                            width: isScreenLg ? '20rem' : '22rem',
                            position: 'relative',
                            '& .MuiTab-root': {
                                color: '#E5673E',
                                alignItems: 'flex-start',
                                justifyContent: 'flex-start',
                                padding: '12px',
                                margin: '5px 0',
                                position: 'relative',
                                overflow: 'visible',
                                zIndex: 1,
                                '&:hover': {
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        top: 1.1,
                                        left: 1.5,
                                        width: '20rem',
                                        height: '60px',
                                        backgroundColor: '#FCF0EC',
                                        zIndex: -1,
                                    },
                                },
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '18rem' : '20rem',
                                    height: '60px',
                                    backgroundColor: 'white',
                                    boxShadow:
                                        '1px 4px 8px rgba(0, 0, 0, 0.2),-1px 4px 8px rgba(0, 0, 0, 0.2)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected': {
                                color: 'white!important',
                                backgroundColor: 'transparent',
                                boxShadow: 'none',
                                zIndex: 2,
                                path: {
                                    fill: 'white',
                                },
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 1.1,
                                    left: 1.5,
                                    width: isScreenLg ? '20rem' : '22rem',
                                    height: '60px',
                                    backgroundImage:
                                        'linear-gradient(90deg, #B75332 30%, #FAE1D8 100%)',
                                    zIndex: -1,
                                },
                            },
                            '& .Mui-selected::before': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        {simulations.map((sim, index) => {
                            const spaceCount = (sim.title.match(/ /g) || []).length;
                            const hasThreeOrMoreSpaces = spaceCount >= 3;

                            return (
                                <Tab
                                    key={index}
                                    icon={
                                        <sim.icon fill={'none'} style={{ marginTop: '0.5rem' }} />
                                    }
                                    iconPosition="start"
                                    label={
                                        <Typography
                                            component="div"
                                            sx={{
                                                width: isScreenLg ? '15rem' : '18rem',
                                                marginTop:
                                                    sim.title === 'Wide Bandwidth Decoupling' ||
                                                    sim.title === 'Capacitor Temperature Rise'
                                                        ? 0
                                                        : isScreenLg &&
                                                            sim.title === 'Capacitor ESR Comparison'
                                                          ? 0
                                                          : hasThreeOrMoreSpaces
                                                            ? 0
                                                            : '0.5rem',
                                                marginLeft: '0.2rem',
                                                fontFamily: 'Poppins',
                                                lineHeight: '20px',
                                                fontSize: isScreenLg ? '18px' : '20px',
                                                fontWeight: 500,
                                                textTransform: 'none',
                                                textAlign: 'left',
                                            }}
                                        >
                                            {sim.title}
                                        </Typography>
                                    }
                                    {...a11yProps(index)}
                                />
                            );
                        })}
                    </Tabs>

                    {simulations.map((sim, index) => (
                        <TabPanel value={value} index={index} key={index}>
                            {generateSimulation(sim)}
                        </TabPanel>
                    ))}
                </Box>
            ) : (
                <Box
                    sx={{
                        flexGrow: 1,
                        bgcolor: '#F8F8F8',
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100%',
                    }}
                >
                    {simulations.map((sim, index) => (
                        <Accordion
                            key={index}
                            expanded={expanded === index}
                            onChange={handleAccordionChange(index)}
                            sx={{
                                marginBottom: '1rem',
                                backgroundColor: '#FAE1D8',
                                width: isScreenMd
                                    ? '650px'
                                    : isScreenSm
                                      ? '422px'
                                      : isScreenXs
                                        ? '295px'
                                        : '833px',
                                borderLeft: '2px solid #E0E0E0',
                                borderRight: '2px solid #E0E0E0',
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    backgroundColor: expanded === index ? '#B75332' : 'white',
                                    color: expanded === index ? 'white' : '#E56731',
                                    '& .MuiSvgIcon-root': {
                                        color: expanded === index ? 'white' : '#E56731',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <sim.icon fill="none" style={{ marginRight: '0.5rem' }} />
                                    <Typography
                                        sx={{
                                            fontSize: isScreenXs ? '14px' : '20px',
                                            fontWeight: 500,
                                        }}
                                    >
                                        {sim.title}
                                    </Typography>
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                                <SingleSimulation
                                    title={sim.title}
                                    img={sim.img}
                                    redirectLink={sim.redirectLink}
                                    buttonText={sim.buttonText}
                                    isNavigate={sim.isNavigate}
                                />
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Box>
            )}
        </>
    );
};
export default KsimOverview;
